import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { showSuccessNotification, showErrorNotification, showWarningNotification } from '../../utils/notifications';

/**
 * Verifica se l'utente può visualizzare gli asset 3D in base al suo ruolo
 * Questa è la versione semplificata usata da UnifiedProductDetail.js
 * @param {string} role - Ruolo dell'utente
 * @returns {boolean} - True se l'utente può visualizzare gli asset 3D
 */
export const canView3DAssets = (role) => {
    return ['Admin', 'Modellista', 'ModellerSupervisor'].includes(role);
};

// Nota: Per VariantDetails.js viene utilizzata la funzione canView3DAssets da RolePermissions.js
// che verifica i permessi nella configurazione centralizzata

/**
 * Genera un file USDZ a partire da un file GLB
 * @param {Object} params - Parametri per la generazione
 * @param {Object} params.variant - Oggetto variante contenente gli URL
 * @param {string} params.key - Chiave dell'URL da generare (urlUsdz o urlUsdzComplete)
 * @param {Function} params.setPendingUploads - Funzione per impostare gli upload in sospeso
 * @param {Function} params.setIsGeneratingUsdz - Funzione per impostare lo stato di generazione
 * @param {Function} params.setEditingKey - Funzione per impostare la chiave in modifica
 * @returns {Promise<void>}
 */
export const handleGenerateUsdz = async ({
    variant,
    key,
    setPendingUploads,
    setIsGeneratingUsdz,
    setEditingKey
}) => {
    try {
        setIsGeneratingUsdz(true);
        setEditingKey(key);

        // Determina quale URL GLB usare come sorgente
        let sourceKey;
        if (key === 'urlUsdz') {
            sourceKey = 'urlGlobal';
        } else if (key === 'urlUsdzComplete') {
            sourceKey = 'urlGlobalComplete';
        } else {
            // Fallback al comportamento precedente per compatibilità
            sourceKey = key === 'urlUsdz' ? 'urlGlobal' : 'urlGlobalComplete';
        }
        const sourceUrl = variant[sourceKey];

        if (!sourceUrl || typeof sourceUrl !== 'string' || sourceUrl.trim() === '') {
            showErrorNotification('Error', `No GLB file found in ${sourceKey}`);
            setIsGeneratingUsdz(false);
            setEditingKey(null);
            return;
        }

        // Estrai il percorso relativo dall'URL
        const firebaseStorageBaseUrl = 'https://firebasestorage.googleapis.com/v0/b/';
        const queryParamIndex = sourceUrl.indexOf('?');
        let relativePath = '';

        // Verifica se l'URL inizia con il base URL di Firebase Storage
        if (sourceUrl.startsWith(firebaseStorageBaseUrl) && queryParamIndex !== -1) {
            // Estrai la parte dell'URL tra '/o/' e '?'
            const startOfPath = sourceUrl.indexOf('/o/');
            if (startOfPath !== -1) {
                relativePath = decodeURIComponent(
                    sourceUrl.substring(startOfPath + 3, queryParamIndex)
                );
            }
        }

        if (!relativePath) {
            showErrorNotification('Error', 'Invalid URL format');
            setIsGeneratingUsdz(false);
            setEditingKey(null);
            return;
        }

        // Rimuovi eventuali slash iniziali
        if (relativePath.startsWith('/')) {
            relativePath = relativePath.substring(1);
        }

        // Verifica che l'API sia raggiungibile
        const converterApiUrl = 'https://glb-to-usdz-converter-251516419347.europe-central2.run.app';

        // Prima verifica se l'API è raggiungibile con una richiesta di test
        try {
            const testResponse = await fetch('https://glb-to-usdz-converter-251516419347.europe-central2.run.app', {
                method: 'GET',
                signal: AbortSignal.timeout(10000) // 10 secondi timeout per il test
            });
        } catch (healthError) {
            // Continua con il tentativo di conversione anche se il health check fallisce
        }

        // Estrai il nome del bucket dall'URL
        const bucketMatch = sourceUrl.match(/\/b\/([^\/]+)\/o\//);
        const bucketName = bucketMatch ? bucketMatch[1] : 'arshadesstaging.appspot.com';

        // Chiama l'API di conversione - usa sia glb_path che glb_url
        const fullApiUrl = `${converterApiUrl}/convert?glb_path=${encodeURIComponent(relativePath)}&glb_url=${encodeURIComponent(sourceUrl)}&bucket=${encodeURIComponent(bucketName)}`;

        // Verifica che il file GLB sia accessibile (skip se CORS issue)
        let glbAccessible = false;
        try {
            const glbTestResponse = await fetch(sourceUrl, {
                method: 'HEAD',
                signal: AbortSignal.timeout(10000)
            });
            if (glbTestResponse.ok) {
                glbAccessible = true;
            }
        } catch (glbError) {
            // Non blocchiamo l'esecuzione, potrebbe essere solo un problema CORS
        }

        let response;
        try {
            response = await fetch(fullApiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                // Aggiungi timeout
                signal: AbortSignal.timeout(60000) // 60 secondi timeout
            });
        } catch (fetchError) {
            if (fetchError.name === 'AbortError') {
                throw new Error('TIMEOUT_ERROR');
            } else if (fetchError.message.includes('Failed to fetch')) {
                // Prova il fallback: costruisci l'URL USDZ basandoti sul percorso GLB
                return await handleUsdzFallback({
                    relativePath,
                    key,
                    setPendingUploads
                });
            } else {
                throw new Error('NETWORK_ERROR');
            }
        }

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            try {
                const errorBody = await response.text();
                if (errorBody) {
                    errorMessage += ` - ${errorBody}`;
                }
            } catch (e) {
                // Could not read error body
            }

            // Se l'API restituisce un errore, prova il fallback
            if (response.status >= 400) {
                return await handleUsdzFallback({
                    relativePath,
                    key,
                    setPendingUploads
                });
            }

            throw new Error('API_ERROR');
        }

        const data = await response.json();

        // Controlla se l'API ha restituito un errore
        if (data.error) {
            throw new Error(`API_ERROR: ${data.error}`);
        }

        // Determina il percorso del file USDZ generato
        let usdzRelativePath = '';
        let usdzUrl = '';

        // Priorità 1: Usa l'URL completo dalla risposta se disponibile
        if (data.usdzUrl) {
            usdzUrl = data.usdzUrl;

            // Estrai anche il percorso relativo per riferimento
            const firebaseStorageBaseUrl = 'https://firebasestorage.googleapis.com/v0/b/';
            const usdzQueryParamIndex = data.usdzUrl.indexOf('?');

            if (data.usdzUrl.startsWith(firebaseStorageBaseUrl) && usdzQueryParamIndex !== -1) {
                // Estrai la parte dell'URL tra '/o/' e '?'
                const startOfPath = data.usdzUrl.indexOf('/o/');
                if (startOfPath !== -1) {
                    usdzRelativePath = decodeURIComponent(
                        data.usdzUrl.substring(startOfPath + 3, usdzQueryParamIndex)
                    );
                }
            }
        }

        // Priorità 2: Usa il percorso USDZ restituito dall'API
        if (!usdzUrl && data.usdz_path) {
            usdzRelativePath = data.usdz_path;
        }

        // Priorità 3: Costruisci il percorso basato sul percorso del file GLB
        if (!usdzUrl && !usdzRelativePath) {
            // Determina il percorso del file USDZ basato sul percorso del file GLB
            const glbPath = relativePath;
            usdzRelativePath = glbPath.replace(/\.glb$/i, '.usdz');

            // Se non termina con .usdz, aggiungi l'estensione
            if (!usdzRelativePath.toLowerCase().endsWith('.usdz')) {
                usdzRelativePath = usdzRelativePath + '.usdz';
            }
        }

        // Ottieni un riferimento al file USDZ in Firebase Storage
        const storage = getStorage();

        // Se abbiamo un URL completo dalla risposta API, usiamo direttamente quello
        if (usdzUrl) {
            // Salva l'URL con il token di accesso
            setPendingUploads(prev => ({
                ...prev,
                [key]: usdzUrl
            }));

            // Mostriamo un messaggio di successo
            showSuccessNotification('Conversion complete!');
            return; // Usciamo dalla funzione se abbiamo avuto successo
        }

        // Altrimenti, proviamo a ottenere un riferimento al file USDZ usando il percorso relativo
        if (!usdzRelativePath) {
            throw new Error('Nessun percorso USDZ valido trovato dalla risposta API');
        }

        try {
            const usdzRef = ref(storage, usdzRelativePath);

            // Genera un nuovo token di accesso per il file USDZ
            const downloadURL = await getDownloadURL(usdzRef);

            // Copia il file USDZ anche nella cartella appropriata accanto al GLB
            try {
                // Determina la cartella di destinazione basata sul tipo di conversione
                let targetFolder;
                if (key === 'urlUsdz') {
                    targetFolder = 'urlGlobal';
                } else if (key === 'urlUsdzComplete') {
                    targetFolder = 'urlGlobalComplete';
                }

                if (targetFolder) {
                    // Costruisci il percorso di destinazione
                    const glbPath = relativePath;
                    const targetUsdzPath = glbPath.replace(/\.glb$/i, '.usdz').replace(`/${targetFolder}/`, `/${targetFolder}/`);

                    // Scarica il file USDZ dalla cartella usdz/
                    const usdzBlob = await fetch(downloadURL).then(response => response.blob());

                    // Carica il file nella cartella organizzata
                    const targetUsdzRef = ref(storage, targetUsdzPath);
                    await uploadBytes(targetUsdzRef, usdzBlob);

                    // Ottieni l'URL della copia organizzata
                    const organizedDownloadURL = await getDownloadURL(targetUsdzRef);

                    // Usa l'URL della copia organizzata invece di quello della cartella usdz/
                    setPendingUploads(prev => ({
                        ...prev,
                        [key]: organizedDownloadURL
                    }));
                } else {
                    // Fallback: usa l'URL originale se non riusciamo a determinare la cartella
                    setPendingUploads(prev => ({
                        ...prev,
                        [key]: downloadURL
                    }));
                }
            } catch (copyError) {
                // Fallback: usa l'URL originale se la copia fallisce
                setPendingUploads(prev => ({
                    ...prev,
                    [key]: downloadURL
                }));
            }

            // Mostriamo un messaggio di successo
            showSuccessNotification('Conversion complete!');
        } catch (storageError) {
            throw new Error(`Unable to find generated USDZ file: ${storageError.message}`);
        }
    } catch (error) {
        // Traduci i codici di errore in messaggi user-friendly
        let userMessage = '';
        if (error.message.startsWith('API_ERROR:')) {
            // Estrai il messaggio di errore specifico dall'API
            const apiError = error.message.replace('API_ERROR: ', '');
            if (apiError.includes('File not found')) {
                userMessage = 'GLB file not found. Please verify the file exists and try again.';
            } else {
                userMessage = `Conversion service error: ${apiError}`;
            }
        } else {
            switch (error.message) {
                case 'CONVERSION_SERVICE_UNAVAILABLE':
                    userMessage = 'Conversion service temporarily unavailable. Please try again later.';
                    break;
                case 'TIMEOUT_ERROR':
                    userMessage = 'Request timeout. The conversion is taking too long, please try again.';
                    break;
                case 'NETWORK_ERROR':
                    userMessage = 'Network error. Please check your connection and try again.';
                    break;
                case 'API_ERROR':
                    userMessage = 'Conversion service error. Please try again later.';
                    break;
                default:
                    // Se non è un codice di errore specifico, usa un messaggio generico
                    if (error.message.includes('GLB file')) {
                        userMessage = 'GLB file not accessible. Please verify the file exists.';
                    } else if (error.message.includes('API')) {
                        userMessage = 'Conversion service error. Please try again later.';
                    } else {
                        userMessage = `An error occurred during USDZ generation: ${error.message}. Please try again.`;
                    }
                    break;
            }
        }

        showErrorNotification('Conversion Error', userMessage);
    } finally {
        // Attendiamo un po' prima di nascondere lo spinner per assicurarci che sia visibile
        setTimeout(() => {
            setIsGeneratingUsdz(false);
            setEditingKey(null);
        }, 1000);
    }
};

/**
 * Funzione di fallback per generare URL USDZ quando l'API non è disponibile
 * @param {Object} params - Parametri per il fallback
 * @param {string} params.relativePath - Percorso relativo del file GLB
 * @param {string} params.key - Chiave dell'URL da generare
 * @param {Function} params.setPendingUploads - Funzione per impostare gli upload in sospeso
 * @returns {Promise<void>}
 */
export const handleUsdzFallback = async ({
    relativePath,
    key,
    setPendingUploads
}) => {
    try {
        // Costruisci il percorso USDZ basandoti sul percorso GLB
        const usdzRelativePath = relativePath.replace(/\.glb$/i, '.usdz');

        // Prova a ottenere un riferimento al file USDZ in Firebase Storage
        const storage = getStorage();
        const usdzRef = ref(storage, usdzRelativePath);

        try {
            // Tenta di ottenere l'URL di download
            const downloadURL = await getDownloadURL(usdzRef);

            // Salva l'URL
            setPendingUploads(prev => ({
                ...prev,
                [key]: downloadURL
            }));

            showSuccessNotification('USDZ file found!', 'The USDZ file was found and linked successfully.');
            return;

        } catch (storageError) {
            console.error('❌ USDZ file not found in storage:', storageError);

            // Se il file non esiste, lancia un errore semplice
            throw new Error('CONVERSION_SERVICE_UNAVAILABLE');
        }
    } catch (error) {
        console.error('❌ Fallback failed:', error);
        // Se l'errore è già un codice, passalo attraverso
        if (error.message === 'CONVERSION_SERVICE_UNAVAILABLE') {
            throw error;
        }
        // Altrimenti, lancia un errore generico
        throw new Error('CONVERSION_SERVICE_UNAVAILABLE');
    }
};

/**
 * Gestisce l'upload di un file GLB o immagine
 * @param {Object} params - Parametri per l'upload
 * @param {File} params.file - File da caricare
 * @param {string} params.skuModel - SKU del modello
 * @param {string} params.recordKey - Chiave del record (urlGlobal, urlGlobalComplete, poster, ecc.)
 * @param {Function} params.setPendingUploads - Funzione per impostare gli upload in sospeso
 * @param {Function} params.setUploadingFiles - Funzione per impostare lo stato di caricamento
 * @returns {Promise<string|null>} - URL del file caricato o null in caso di errore
 */
export const handleFileUpload = async ({
    file,
    skuModel,
    recordKey,
    setPendingUploads,
    setUploadingFiles
}) => {
    if (!file) return null;

    try {
        setUploadingFiles?.(prev => ({
            ...prev,
            [recordKey]: true
        }));

        const storage = getStorage();
        let folderType = '';
        let folderName = '';

        // Determina il tipo di cartella in base alla chiave del record
        if (['urlGlobal', 'urlGlobalComplete'].includes(recordKey)) {
            folderType = '3dModels';
            folderName = recordKey;
        } else if (['poster', 'poster2', 'poster3', 'poster4', 'posterNoBackground'].includes(recordKey)) {
            folderType = 'referenceImages';
            folderName = recordKey;
        } else {
            throw new Error(`Invalid record key: ${recordKey}`);
        }

        // Costruisci il percorso del file
        const path = `ARS_Library_Product_Data/${skuModel}/${folderType}/${folderName}/${file.name}`;
        const storageRef = ref(storage, path);

        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        setPendingUploads(prev => ({
            ...prev,
            [recordKey]: downloadURL
        }));

        setUploadingFiles?.(prev => {
            const updated = { ...prev };
            delete updated[recordKey];
            return updated;
        });

        return downloadURL;
    } catch (err) {
        console.error("Upload error:", err);
        setUploadingFiles?.(prev => {
            const updated = { ...prev };
            delete updated[recordKey];
            return updated;
        });
        showErrorNotification("Upload Error", `Failed to upload ${file.name}: ${err.message}`);
        return null;
    }
};

/**
 * Genera lo stile per i bottoni con effetto hover
 * @param {boolean} disabled - Se il bottone è disabilitato
 * @param {Object} baseStyle - Stile di base
 * @param {Object} primaryShades - Colori primari
 * @returns {Object} - Stile del bottone
 */
export const getButtonWithHoverStyle = (disabled, baseStyle = {}, primaryShades = { primary500: '#1890ff' }) => ({
    ...baseStyle,
    width: '32px',
    height: '32px',
    minWidth: '32px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #d9d9d9',
    borderRadius: '6px',
    transition: 'all 0.3s',
    ':hover': disabled ? {} : {
        borderColor: primaryShades.primary500,
        color: primaryShades.primary500
    }
});

/**
 * Verifica se un URL è valido
 * @param {string} url - URL da verificare
 * @returns {boolean} - True se l'URL è valido
 */
export const isValidUrl = (string) => {
    return string && typeof string === 'string' && string.startsWith('http');
};

/**
 * Verifica se un'immagine è valida
 * @param {string} url - URL dell'immagine
 * @returns {Promise<boolean>} - Promise che si risolve in true se l'immagine è valida
 */
export const checkImageValidity = (url) => {
    return new Promise((resolve) => {
        if (!url) {
            resolve(false);
            return;
        }

        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
    });
}; 